import { jwtDecode } from 'jwt-decode'
import { storeToRefs } from 'pinia'
import type { AuthConfig, AuthTokens, User } from '../../types'
import { useAuthStore } from '@/stores/auth'

export default class AuthStorage {
  private authConfig
  private config
  public authStore
  private storage: Storage

  static ACCESS_TOKEN_STORAGE_KEY = 'auth.access_token'
  static REFRESH_TOKEN_STORAGE_KEY = 'auth.refresh_token'
  static ACCESS_TOKEN_EXP_KEY = 'auth.access_token_expiration'
  static REFRESH_TOKEN_EXP_KEY = 'auth.refresh_token_expiration'
  static FLAG_PERSISTENT_KEY = 'auth.persistent'
  static PERSISTENT_VALUE_TRUE = 'yes'
  static PERSISTENT_VALUE_FALSE = 'no'
  static REDIRECT_KEY = 'auth.redirect'

  constructor({ authConfig }: { authConfig: AuthConfig }) {
    this.authConfig = authConfig
    this.config = authConfig.cookie
    this.authStore = useAuthStore()

    // Khởi tạo storage dựa trên giá trị persistent từ localStorage, mặc định là true
    const persistentValue = localStorage.getItem(AuthStorage.FLAG_PERSISTENT_KEY) ?? AuthStorage.PERSISTENT_VALUE_TRUE

    this.storage = persistentValue === AuthStorage.PERSISTENT_VALUE_TRUE ? localStorage : sessionStorage
  }

  get referer() {
    return this.storage.getItem(AuthStorage.REDIRECT_KEY) ?? null
  }

  set referer(url: string | null) {
    if (url)
      this.storage.setItem(AuthStorage.REDIRECT_KEY, url)
    else
      this.storage.removeItem(AuthStorage.REDIRECT_KEY)
  }

  get accessToken() {
    if (this.isAccessTokenExpired())
      return null

    return this.storage.getItem(AuthStorage.ACCESS_TOKEN_STORAGE_KEY) ?? null
  }

  set accessToken(value: string | null) {
    if (value) {
      this.storage.setItem(AuthStorage.ACCESS_TOKEN_STORAGE_KEY, value)

      const accessTokenExpiration = this.maxAgeToDate(this.accessTokenMaxAge(value)).getTime()

      this.storage.setItem(AuthStorage.ACCESS_TOKEN_EXP_KEY, String(accessTokenExpiration))
    }
    else {
      this.storage.removeItem(AuthStorage.ACCESS_TOKEN_STORAGE_KEY)
      this.storage.removeItem(AuthStorage.ACCESS_TOKEN_EXP_KEY)
    }
  }

  accessTokenMaxAge(token?: string) {
    const accessToken = token ?? this.accessToken

    return accessToken ? this.cookieMaxAge('token', accessToken) : -1
  }

  get refreshToken() {
    if (this.isRefreshTokenExpired())
      return null

    return this.storage.getItem(AuthStorage.REFRESH_TOKEN_STORAGE_KEY) ?? null
  }

  set refreshToken(value: string | null) {
    if (value) {
      this.storage.setItem(AuthStorage.REFRESH_TOKEN_STORAGE_KEY, value)

      const refreshTokenExpiration = this.maxAgeToDate(this.refreshTokenMaxAge(value)).getTime()

      this.storage.setItem(AuthStorage.REFRESH_TOKEN_EXP_KEY, String(refreshTokenExpiration))
    }
    else {
      this.storage.removeItem(AuthStorage.REFRESH_TOKEN_STORAGE_KEY)
      this.storage.removeItem(AuthStorage.REFRESH_TOKEN_EXP_KEY)
    }
  }

  refreshTokenMaxAge(token?: string) {
    const refreshToken = token ?? this.refreshToken

    return refreshToken ? this.cookieMaxAge('refreshToken', refreshToken) : -1
  }

  get accessTokenExpiration() {
    return Number(this.storage.getItem(AuthStorage.ACCESS_TOKEN_EXP_KEY)) ?? null
  }

  get refreshTokenExpiration() {
    return Number(this.storage.getItem(AuthStorage.REFRESH_TOKEN_EXP_KEY)) ?? null
  }

  get persistent() {
    return this.storage.getItem(AuthStorage.FLAG_PERSISTENT_KEY) ?? null
  }

  set persistent(value: string | null) {
    if (value) {
      this.storage.setItem(AuthStorage.FLAG_PERSISTENT_KEY, value)
      this.storage = value === AuthStorage.PERSISTENT_VALUE_TRUE ? localStorage : sessionStorage
    }
    else {
      this.storage.removeItem(AuthStorage.FLAG_PERSISTENT_KEY)
      this.storage = sessionStorage
    }
  }

  accessTokenExpires() {
    return this.accessTokenExpiration
      ? new Date(this.accessTokenExpiration)
      : null
  }

  refreshTokenExpires() {
    return this.refreshTokenExpiration
      ? new Date(this.refreshTokenExpiration)
      : null
  }

  isAccessTokenExpired() {
    const accessTokenExp = this.accessTokenExpires()
    if (!accessTokenExp)
      return true

    return accessTokenExp < new Date()
  }

  isRefreshTokenExpired() {
    const refreshTokenExp = this.refreshTokenExpires()
    if (!refreshTokenExp)
      return true

    return refreshTokenExp < new Date()
  }

  get user() {
    const store = storeToRefs(this.authStore) as any

    return store.user
  }

  get loggedIn(): Ref<boolean> {
    const store = storeToRefs(this.authStore) as any

    return store.loggedIn
  }

  get isAdmin(): Ref<boolean> {
    const store = storeToRefs(this.authStore) as any

    return store.isAdmin
  }

  get waiting2FA(): Ref<boolean> {
    const store = storeToRefs(this.authStore) as any

    return store.waiting2FA
  }

  set waiting2FA(value: boolean) {
    this.authStore.setWaiting2FA(value)
  }

  setAuth({ token, refreshToken }: AuthTokens) {
    this.accessToken = token
    this.refreshToken = refreshToken
    this.authStore.setWaiting2FA(this.accessToken ? jwtDecode<{ waiting_2fa: boolean }>(this.accessToken).waiting_2fa : false)
  }

  setUser(user: User | null) {
    this.authStore.setUser(user)
  }

  resetAuth() {
    this.accessToken = null
    this.refreshToken = null
    this.referer = null
    this.authStore.setUser(null)
    this.authStore.setWaiting2FA(false)
  }

  setPersistent(value: boolean | null) {
    const storageValue = value
      ? AuthStorage.PERSISTENT_VALUE_TRUE
      : AuthStorage.PERSISTENT_VALUE_FALSE

    this.persistent = storageValue
  }

  private cookieMaxAge(tokenType: 'token' | 'refreshToken', token?: string) {
    try {
      if (!token)
        throw new Error('Token is not provided')

      const decoded = jwtDecode<{ exp: number }>(token)

      return decoded.exp - Math.ceil(Date.now() / 1000)
    }
    catch {
      return this.authConfig[tokenType].maxAge || this.config.maxAge
    }
  }

  private maxAgeToDate(maxAge: number) {
    return new Date(Date.now() + maxAge * 1000)
  }
}
